import { useMemo, useState } from "react";
import { Dialog, IconButton, TextField } from "@mui/material";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService from "api/budgetService";
import { budgetDialogPaperProps } from "components/Budget/budgetDialog";
import { IBudgetEngagement } from "dto/Budget/IBudgetEngagement";
import { formatMoney } from "utils/formatMoney";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

interface Props {
  engagement: IBudgetEngagement;
  onClose: () => void;
  onCreated?: () => void;
}

const CreateEngagementCorrectionModal = ({
  engagement,
  onClose,
  onCreated,
}: Props) => {
  const { data: expense } = budgetService.useGetExpense(engagement.ExpenseId);
  const { data: paragraphs = [] } = budgetService.useGetParagraphs();
  const { data: existingEngagements = [] } = budgetService.useGetEngagements(
    engagement.ExpenseId,
  );
  const { mutate: createEngagement, isLoading } =
    budgetService.useCreateEngagement();

  const initialAmounts = useMemo(() => {
    const map: Record<string, string> = {};
    engagement.Quotas.forEach((q) => {
      map[q.ExpenseQuotaId] = String(q.Amount);
    });
    return map;
  }, [engagement]);

  const [amounts, setAmounts] = useState<Record<string, string>>(initialAmounts);
  const [justification, setJustification] = useState("");

  const paragraphLabel = (paragraphId: string, suffix: string) => {
    const p = paragraphs.find((x) => x.Id === paragraphId);
    return `${p?.Symbol ?? "?"}${suffix !== "0" ? suffix : ""} - ${p?.Name ?? ""}`;
  };

  // Środki zaangażowane na paragrafie przez inne zaangażowania (bez korygowanego).
  const engagedByOthers = useMemo(() => {
    const map: Record<string, number> = {};
    existingEngagements
      .filter((e) => e.Id !== engagement.Id)
      .flatMap((e) => e.Quotas)
      .forEach((q) => {
        map[q.ExpenseQuotaId] = (map[q.ExpenseQuotaId] ?? 0) + q.Amount;
      });
    return map;
  }, [existingEngagements, engagement.Id]);

  const freeByQuota = (quotaId: string, planned: number) =>
    planned - (engagedByOthers[quotaId] ?? 0);

  const quotas = expense?.Quotas ?? [];

  const total = useMemo(
    () =>
      Object.values(amounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0),
    [amounts],
  );

  const exceedsFree = quotas.some((q) => {
    const entered = parseFloat(amounts[q.Id]) || 0;
    return entered > freeByQuota(q.Id, q.Amount);
  });

  const canSubmit = total > 0 && !exceedsFree;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createEngagement(
      {
        ExpenseId: engagement.ExpenseId,
        Title: engagement.Title,
        EngagementDate: engagement.EngagementDate,
        ExecutionDeadline: engagement.ExecutionDeadline,
        GrantingProcedure: engagement.GrantingProcedure,
        GrantingProcedureDescription: engagement.GrantingProcedureDescription,
        Justification: justification.trim() || undefined,
        CorrectedEngagementId: engagement.Id,
        Quotas: quotas
          .filter((q) => (parseFloat(amounts[q.Id]) || 0) > 0)
          .map((q) => ({
            ExpenseQuotaId: q.Id,
            Amount: parseFloat(amounts[q.Id]),
          })),
      },
      {
        onSuccess: () => {
          onCreated?.();
          onClose();
        },
        onError: () =>
          notification({
            title: "Wystąpił błąd",
            text: "Nie udało się utworzyć korekty.",
            type: ENotificationType.ERROR,
          }),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={budgetDialogPaperProps}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>Korekta zaangażowania</span>
          <IconButton onClick={onClose} size="small" className={styles.close}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>

        <div className={styles.engagementTitle}>{engagement.Title}</div>
        {engagement.Signature && (
          <div className={styles.signature}>{engagement.Signature}</div>
        )}

        <div className={styles.content}>
          <span className={styles.sectionTitle}>Paragrafy</span>
          {quotas.map((q) => {
            const free = freeByQuota(q.Id, q.Amount);
            const entered = parseFloat(amounts[q.Id]) || 0;
            const over = entered > free;
            return (
              <div className={styles.quota} key={q.Id}>
                <div className={styles.quotaInfo}>
                  <span className={styles.quotaLabel}>
                    {paragraphLabel(q.ParagraphId, q.ParagraphSuffix)}
                  </span>
                  <span className={styles.free}>
                    Wolne środki {formatMoney(free)}
                  </span>
                </div>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  error={over}
                  helperText={over ? "Kwota przekracza wolne środki" : undefined}
                  value={amounts[q.Id] ?? ""}
                  onChange={(e) =>
                    setAmounts((prev) => ({ ...prev, [q.Id]: e.target.value }))
                  }
                />
              </div>
            );
          })}

          <TextField
            label="Uzasadnienie korekty"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />

          <div className={styles.total}>
            <span>Zaangażowane środki po korekcie</span>
            <strong>{formatMoney(total)}</strong>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.submit}
            disabled={!canSubmit || isLoading}
            onClick={handleSubmit}
          >
            Przekaż do akceptacji
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateEngagementCorrectionModal;
